import { useState, useMemo, useId } from "react";
import { wheelMembers } from "../lib/poolMembers";

function colorKey(color) {
    return (color || "default").replace(/[^a-zA-Z0-9]/g, "");
}

/**
 * Thin, minimalist SVG donut "Split Wheel". Per-segment colored glow;
 * hovered / linked recipient gets stronger emphasis.
 */
export function SplitWheel({ members = [], size = 400, stroke = 11, highlightId, onHover, ariaLabel = "Profit allocation", glowBleed = 0 }) {
    const [hoverId, setHoverId] = useState(null);
    const activeId = hoverId ?? highlightId ?? null;
    const instanceId = useId().replace(/:/g, "");

    const radius = (size - stroke - 6) / 2;
    const circumference = 2 * Math.PI * radius;
    const center = size / 2;

    const active = wheelMembers(members);
    const segments = useMemo(() => {
        let offset = 0;
        return active.map((m) => {
            const length = (m.percentage / 100) * circumference;
            const dasharray = `${length} ${circumference - length}`;
            const dashoffset = -offset;
            offset += length;
            return { ...m, dasharray, dashoffset, length };
        });
    }, [active, circumference]);

    const glowColors = useMemo(
        () => [...new Set(segments.map((s) => s.color).filter(Boolean))],
        [segments],
    );

    const focused = active.find((m) => m.id === activeId) ?? active[0];
    const centerRole = (focused?.role_label || "").trim();
    const bleed = Math.max(0, glowBleed);
    const outerSize = size + bleed * 2;

    return (
        <div
            className="split-wheel mx-auto flex shrink-0 justify-center overflow-visible"
            style={{ width: outerSize, maxWidth: "100%" }}
            data-testid="split-wheel"
            aria-label={ariaLabel}
        >
            <div
                className="split-wheel__canvas relative shrink-0 overflow-visible"
                style={{
                    width: outerSize,
                    height: outerSize,
                    maxWidth: "100%",
                }}
            >
                <div
                    className="relative overflow-visible"
                    style={{
                        width: size,
                        height: size,
                        margin: bleed,
                    }}
                >
                <svg
                    viewBox={`0 0 ${size} ${size}`}
                    width={size}
                    height={size}
                    className="block h-auto w-full max-w-full -rotate-90 overflow-visible"
                    aria-hidden
                >
                    <defs>
                        {glowColors.map((color) => {
                            const key = colorKey(color);
                            return (
                                <g key={color}>
                                    <filter id={`fs-glow-soft-${instanceId}-${key}`} x="-80%" y="-80%" width="260%" height="260%" colorInterpolationFilters="sRGB">
                                        <feDropShadow dx="0" dy="0" stdDeviation="2" floodColor={color} floodOpacity="0.55" />
                                    </filter>
                                    <filter id={`fs-glow-strong-${instanceId}-${key}`} x="-90%" y="-90%" width="280%" height="280%" colorInterpolationFilters="sRGB">
                                        <feDropShadow dx="0" dy="0" stdDeviation="3.5" floodColor={color} floodOpacity="0.95" />
                                        <feDropShadow dx="0" dy="0" stdDeviation="6" floodColor={color} floodOpacity="0.35" />
                                    </filter>
                                </g>
                            );
                        })}
                    </defs>
                    <circle cx={center} cy={center} r={radius} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth={stroke} />
                    {segments.map((s) => {
                        const isHighlighted = activeId === s.id;
                        const dimmed = !!activeId && !isHighlighted;
                        const key = colorKey(s.color);
                        const filterId = isHighlighted
                            ? `fs-glow-strong-${instanceId}-${key}`
                            : `fs-glow-soft-${instanceId}-${key}`;
                        return (
                            <circle
                                key={s.id}
                                cx={center}
                                cy={center}
                                r={radius}
                                fill="none"
                                stroke={s.color}
                                strokeWidth={isHighlighted ? stroke + 1.25 : stroke + 0.75}
                                strokeDasharray={s.dasharray}
                                strokeDashoffset={s.dashoffset}
                                strokeLinecap="butt"
                                filter={`url(#${filterId})`}
                                style={{
                                    transition: "opacity 220ms ease, stroke-width 220ms ease",
                                    opacity: dimmed ? 0.42 : 1,
                                    cursor: "pointer",
                                }}
                                onMouseEnter={() => {
                                    setHoverId(s.id);
                                    if (highlightId !== s.id) onHover?.(s.id);
                                }}
                                onMouseLeave={() => {
                                    setHoverId(null);
                                    if (highlightId != null) onHover?.(null);
                                }}
                                data-testid={`wheel-slice-${s.id}`}
                            />
                        );
                    })}
                </svg>
                <div className="pointer-events-none absolute inset-0 grid place-items-center px-5">
                    <div className="text-center">
                        <p
                            className={`mb-1.5 h-4 text-[0.7rem] font-medium uppercase tracking-[0.18em] ${centerRole ? "" : "invisible"}`}
                            style={{ color: focused?.color || "rgba(255,255,255,0.55)" }}
                        >
                            {centerRole || "—"}
                        </p>
                        <p
                            className="font-heading text-4xl sm:text-5xl tracking-tight font-mono tabular-nums min-w-[4.5ch]"
                            style={{ color: focused?.color }}
                            data-testid="wheel-center-percent"
                        >
                            {focused ? `${focused.percentage}%` : "—"}
                        </p>
                        <p className="mt-1 h-5 text-sm text-white/80 font-medium truncate max-w-[9rem] mx-auto">{focused?.name || ""}</p>
                    </div>
                </div>
                </div>
            </div>
        </div>
    );
}
